import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import {
  Package, Layers, CheckCircle, DollarSign, RefreshCw, Search,
  ExternalLink, Boxes, Sparkles,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

// Supported catalog locales (same as the scraper). Used for the currency symbol.
const LOCALES = [
  { id: 'en-us', label: 'US', flag: '🇺🇸', sym: '$' },
  { id: 'en-gb', label: 'UK', flag: '🇬🇧', sym: '£' },
];
const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');

// Fixed palette for category badges (deterministic selection from the name)
const CAT_COLORS = [
  '#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6',
  '#ef4444', '#14b8a6', '#f97316', '#84cc16', '#06b6d4', '#a855f7',
];
const catColor = (name) => {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) % CAT_COLORS.length;
  return CAT_COLORS[h];
};

function StatTile({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-4">
      <div className="rounded-lg p-3" style={{ backgroundColor: `${color}15`, color }}>
        <Icon size={22} />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-bold text-gray-900 leading-tight">{value}</div>
        <div className="text-xs text-gray-500">{label}</div>
        {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

export default function PlaymobilCatalog() {
  // The API is HUPALUPA-gated and identifies the brand from this header, so it has to be sent.
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('');       // '' = all
  const [activeCollab, setActiveCollab] = useState(''); // '' = all (filter axis separate from category)
  const [locale, setLocale] = useState('en-us');

  const curSym = (LOCALES.find((l) => l.id === locale) || LOCALES[0]).sym;
  const money = useCallback(
    (v) => (v == null ? '—' : `${curSym}${Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}`),
    [curSym]
  );

  const fetchData = useCallback(async () => {
    if (!brandId) return; // brand not resolved yet — the gated API would 403
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/playmobil/catalog?locale=${locale}`, {
        credentials: 'include',
        headers: { 'x-brand-id': brandId },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load catalog: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [locale, brandId]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // Reset filters when the locale changes (categories/collabs are locale-specific)
  useEffect(() => { setActiveCat(''); setActiveCollab(''); setSearch(''); }, [locale]);

  const products = useMemo(() => {
    let p = data?.products || [];
    if (activeCollab) p = p.filter((x) => x.collab === activeCollab);
    if (activeCat) p = p.filter((x) => (x.category || '(Uncategorized)') === activeCat);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      p = p.filter(
        (x) =>
          (x.product_name || '').toLowerCase().includes(q) ||
          (x.sku || '').toLowerCase().includes(q) ||
          (x.description || '').toLowerCase().includes(q)
      );
    }
    return p;
  }, [data, activeCat, activeCollab, search]);

  const s = data?.summary;

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Country tabs — split the page into US / UK */}
      <div className="flex gap-1 mb-5 border-b border-gray-200">
        {LOCALES.map((l) => {
          const on = locale === l.id;
          return (
            <button
              key={l.id}
              onClick={() => setLocale(l.id)}
              className={`flex items-center gap-2 px-5 py-3 text-[15px] font-semibold border-b-2 -mb-px transition ${
                on ? 'border-amber-500 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              <span className="text-lg leading-none">{l.flag}</span>
              Playmobil {l.label}
              {on && s && (
                <span className="ml-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                  {fmtNum(s.totalProducts)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-amber-100 text-amber-600 p-2.5">
            <Boxes size={26} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Playmobil Catalog ({(LOCALES.find((l) => l.id === locale) || LOCALES[0]).label})
            </h1>
            <p className="text-sm text-gray-500">
              playmobil.com/{locale} — anonymous scrape
              {data?.fetchDate && ` · last updated ${String(data.fetchDate).slice(0, 10)}`}
            </p>
          </div>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-gray-900 text-white hover:bg-gray-700"
        >
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loading && !data && (
        <div className="text-center text-gray-400 py-20">Loading…</div>
      )}
      {error && !loading && (
        <div className="text-center text-red-500 py-20">Error: {error}</div>
      )}
      {!loading && !error && data && !s && (
        <div className="text-center text-gray-400 py-20">
          No data has been fetched yet for this locale ({locale}).
        </div>
      )}

      {s && (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatTile icon={Package} color="#6366f1" label="Total Products" value={fmtNum(s.totalProducts)} />
            <StatTile icon={Layers} color="#ec4899" label="Categories" value={fmtNum(s.totalCategories)} />
            <StatTile
              icon={CheckCircle} color="#10b981" label="In Stock"
              value={fmtNum(s.inStock)}
              sub={`${Math.round((s.inStock / s.totalProducts) * 100)}% available`}
            />
            <StatTile
              icon={DollarSign} color="#f59e0b" label="Avg. Price"
              value={money(s.avgPrice)}
              sub={`${money(s.minPrice)} – ${money(s.maxPrice)}`}
            />
          </div>

          {/* Collaborations / licensed series — shown before categories */}
          {(data.collaborations || []).length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-fuchsia-500" />
                  <span className="text-sm font-semibold text-gray-700">Collaborations & Licensed Series</span>
                  <span className="text-xs text-gray-400">
                    {data.collaborations.length} brands ·{' '}
                    {fmtNum(data.collaborations.reduce((a, c) => a + c.productCount, 0))} products
                  </span>
                </div>
                {activeCollab && (
                  <button
                    onClick={() => setActiveCollab('')}
                    className="text-xs text-gray-500 hover:text-gray-800 underline"
                  >
                    Clear filter
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {data.collaborations.map((c) => {
                  const on = activeCollab === c.label;
                  return (
                    <button
                      key={c.label}
                      onClick={() => { setActiveCollab(on ? '' : c.label); setActiveCat(''); }}
                      className={`text-left rounded-xl border p-3 transition flex flex-col gap-2 ${
                        on ? 'border-fuchsia-400 ring-2 ring-fuchsia-100 bg-fuchsia-50/40' : 'border-gray-100 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                          {c.image ? (
                            <img src={c.image} alt={c.label} loading="lazy" className="w-full h-full object-contain p-0.5"
                              onError={(e) => { e.target.style.display = 'none'; }} />
                          ) : (
                            <Sparkles size={16} className="text-gray-300" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-sm text-gray-900 truncate" title={c.label}>{c.label}</div>
                          <div className="text-[11px] text-gray-400">{fmtNum(c.inStockCount)} in stock</div>
                        </div>
                      </div>
                      <div className="flex items-end justify-between">
                        <div className="text-2xl font-bold text-gray-900 leading-none">{fmtNum(c.productCount)}</div>
                        <div className="text-[11px] text-gray-500 text-right">
                          {c.minPrice != null && (
                            c.minPrice === c.maxPrice ? money(c.minPrice) : `${money(c.minPrice)}–${money(c.maxPrice)}`
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Category summary strip */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-5">
            <div className="text-sm font-semibold text-gray-700 mb-3">Summary by Category</div>
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => { setActiveCat(''); setActiveCollab(''); }}
                className={`text-xs px-3 py-1.5 rounded-full border ${
                  activeCat === '' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'
                }`}
              >
                All ({fmtNum(s.totalProducts)})
              </button>
              {(data.categories || []).map((c) => (
                <button
                  key={c.category}
                  onClick={() => { setActiveCat(activeCat === c.category ? '' : c.category); setActiveCollab(''); }}
                  className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5 ${
                    activeCat === c.category ? 'text-white border-transparent' : 'bg-white text-gray-700 border-gray-200'
                  }`}
                  style={activeCat === c.category ? { backgroundColor: catColor(c.category) } : {}}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: catColor(c.category) }}
                  />
                  {c.category} ({c.productCount})
                </button>
              ))}
            </div>

            {/* Category table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-100">
                    <th className="py-2 pr-4 font-medium">Category</th>
                    <th className="py-2 pr-4 font-medium text-right">Products</th>
                    <th className="py-2 pr-4 font-medium text-right">In Stock</th>
                    <th className="py-2 pr-4 font-medium text-right">Avg. Price</th>
                    <th className="py-2 pr-4 font-medium text-right">Min–Max</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.categories || []).map((c) => (
                    <tr
                      key={c.category}
                      onClick={() => { setActiveCat(activeCat === c.category ? '' : c.category); setActiveCollab(''); }}
                      className={`border-b border-gray-50 cursor-pointer hover:bg-gray-50 ${
                        activeCat === c.category ? 'bg-gray-50' : ''
                      }`}
                    >
                      <td className="py-2 pr-4">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: catColor(c.category) }} />
                          {c.category}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-right font-medium">{fmtNum(c.productCount)}</td>
                      <td className="py-2 pr-4 text-right text-gray-500">{fmtNum(c.inStockCount)}</td>
                      <td className="py-2 pr-4 text-right">{money(c.avgPrice)}</td>
                      <td className="py-2 pr-4 text-right text-gray-500">
                        {money(c.minPrice)} – {money(c.maxPrice)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Search */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search product name, SKU or description…"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
            </div>
            <div className="text-sm text-gray-500">{fmtNum(products.length)} products shown</div>
          </div>

          {/* Product grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {products.map((p) => (
              <div key={p.sku} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
                <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden">
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt={p.product_name}
                      loading="lazy"
                      className="w-full h-full object-contain p-2"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <Package size={40} className="text-gray-300" />
                  )}
                </div>
                <div className="p-3 flex flex-col flex-1">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    {p.category && (
                      <span
                        className="text-[10px] font-medium px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: catColor(p.category) }}
                      >
                        {p.category}
                      </span>
                    )}
                    <span className={`text-[10px] font-medium ${p.in_stock ? 'text-green-600' : 'text-gray-400'}`}>
                      {p.in_stock ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </div>
                  <div className="font-semibold text-sm text-gray-900 leading-snug line-clamp-2" title={p.product_name}>
                    {p.product_name}
                  </div>
                  <div className="text-[11px] text-gray-400 mt-0.5">SKU {p.sku}</div>
                  {p.description && (
                    <p className="text-xs text-gray-500 mt-2 line-clamp-3">{p.description}</p>
                  )}
                  <div className="flex items-center justify-between mt-auto pt-3">
                    <span className="text-lg font-bold text-gray-900">{money(p.price)}</span>
                    {p.product_url && (
                      <a
                        href={p.product_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1"
                      >
                        Open <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {products.length === 0 && (
            <div className="text-center text-gray-400 py-16">No matching products.</div>
          )}
        </>
      )}
    </div>
  );
}
